<template>
  <div class="ai-panel">
    <div class="ai-body">
      <div
        v-if="formVisible"
        class="ai-form">
        <div
          v-if="creditsError || generationError"
          class="ai-alert"
          role="alert">
          <icon name="alert-circle"/>
          <span>{{ creditsError || generationError }}</span>
        </div>
        <div class="ai-field">
          <label
            class="ai-label"
            for="ai-image-description">Describe the image you want to generate</label>
          <textarea
            id="ai-image-description"
            v-model="description"
            :class="{ 'is-invalid': showDescriptionError }"
            class="ai-textarea"
            placeholder="e.g. A friendly bank teller helping a customer, bright modern branch"
            @input="showDescriptionError = false"
          />
          <div
            v-if="showDescriptionError"
            class="ai-field-error">
            <icon name="alert-circle"/>
            <span>Description must not be empty.</span>
          </div>
        </div>
        <div class="ai-notice">
          <p>
            AI image generation is provided by Google Nano Banana 2. While Google Nano Banana 2 permits commercial use of its generated images, we cannot guarantee that every image fully complies with all applicable copyright or trademark laws.
          </p>
          <p>
            Please note that images generated with Google Nano Banana 2 are not covered by the same licensing protections as those in our regular stock photo library. By proceeding, you acknowledge this and accept full responsibility for the commercial use of any Google Nano Banana 2 generated images.
          </p>
          <p class="ai-notice-cta">Click “Accept” to continue.</p>
        </div>
        <div>
          <label
            class="ai-accept"
            for="acceptTerms">
            <input
              id="acceptTerms"
              v-model="acceptTerms"
              type="checkbox"
            >
            <span>Accept</span>
          </label>
          <div
            v-if="showTermsError"
            class="ai-terms-error">
            Please accept the terms to proceed.
          </div>
        </div>
        <div class="ai-generate-row">
          <button
            :disabled="isLoading"
            class="ai-generate"
            type="button"
            @click="generateImage"
          >
            <icon name="sparkle"/>
            Generate Images
          </button>
        </div>
      </div>

      <div
        v-if="resultsVisible"
        ref="imageGrid"
        class="ai-results">
        <div
          v-if="creditsError || generationError"
          class="ai-alert"
          role="alert">
          <icon name="alert-circle"/>
          <span>{{ creditsError || generationError }}</span>
        </div>
        <div class="ai-results-bar">
          <span class="ai-prompt">“<span class="ai-prompt-text">{{ description }}</span>”</span>
          <button
            class="ai-new-prompt"
            type="button"
            @click="startNewPrompt">
            <icon name="pencil"/>
            New prompt
          </button>
        </div>
        <div class="ai-grid">
          <div
            v-for="(image, index) in images"
            :key="image.id + '-' + index"
            :class="['ai-tile', 'image-wrapper', { zoomed: zoomedIndex === index }]">
            <button
              :aria-label="'Add generated image ' + (index + 1) + ' to canvas'"
              class="ai-tile-hit"
              type="button"
              @click="addToCanvas(index)">
              <img
                :src="image.url"
                :style="imageStyle(image)"
                alt="">
            </button>
            <button
              aria-label="Zoom image"
              class="ai-zoom"
              title="Zoom image"
              type="button"
              @click.stop="toggleZoom(index)">
              <icon name="zoom"/>
            </button>
            <span class="ai-badge">Click to add</span>
          </div>
          <div
            v-for="n in placeholderCount"
            :key="'placeholder-' + n"
            class="ai-skel">
            <span
              class="ai-spin"
              aria-hidden="true"/>
          </div>
        </div>
      </div>
    </div>

    <div class="ai-credits">
      <icon name="sparkle"/>
      <span>Credits remaining: <b>{{ remainingCredits }} / {{ maxCredits }}</b>. Credits update {{ firstOfNextMonth }}.</span>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import EventBus from '../../EventBus';

export default {
  data() {
    return {
      acceptTerms:          false,
      description:          '',
      showResult:           false,
      isLoading:            false,
      showForm:             true,
      showDescriptionError: false,
      showTermsError:       false,
      addingToCanvas:       false,
      images:               [],
      creditsError:         '',
      generationError:      '',
      maxCredits:           0,
      remainingCredits:     0,
      zoomedIndex:          null,
    };
  },
  computed: {
    ...mapGetters({ isAddNewImageModal: 'getIsAddNewImageModal' }),
    firstOfNextMonth() {
      const today = new Date();
      // Move to next month
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

      // Format MM/DD/YY
      const mm = String(nextMonth.getMonth() + 1).padStart(2, '0');
      const dd = String(nextMonth.getDate()).padStart(2, '0');
      const yy = String(nextMonth.getFullYear()).slice(-2);

      return `${mm}/${dd}/${yy}`;
    },
    formVisible() {
      if (this.showForm) return true;
      return !!(this.creditsError || this.generationError) && !this.images.length && !this.isLoading;
    },
    resultsVisible() {
      return this.showResult && !this.formVisible;
    },
    placeholderCount() {
      if (!this.isLoading) return 0;
      return Math.max(0, 4 - this.images.length);
    },
  },
  watch: {
    acceptTerms() {
      this.showTermsError = false;
    },
  },
  mounted() {
    this.updateCredits();
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    imageStyle(image) {
      const width  = Number(image.width);
      const height = Number(image.height);
      if (!(width > 0 && height > 0)) return {};
      return { aspectRatio: `${width} / ${height}` };
    },
    toggleZoom(index) {
      this.zoomedIndex =          this.zoomedIndex === index ? null : index;
    },
    startNewPrompt() {
      this.showResult      = false;
      this.showForm        = true;
      this.images          = [];
      this.generationError = '';
      this.creditsError    = '';
      this.zoomedIndex     = null;
    },
    handleOutsideClick(event) {
      const zoomedEl = this.$el.querySelector('.image-wrapper.zoomed');

      if (zoomedEl && !zoomedEl.contains(event.target)) {
        this.zoomedIndex = null;
      }
    },
    generateImage() {
      this.showDescriptionError = this.description.trim() === '';
      this.showTermsError       = !this.acceptTerms;
      if (this.showDescriptionError || this.showTermsError) {
        return;
      }

      this.isLoading       = true;
      this.showForm        = false;
      this.showResult      = true; // show results right away
      this.images          = [];
      this.creditsError    = '';
      this.generationError = '';
      this.zoomedIndex     = null;
      this.allUrls         = []; // reset (you need to bind this to your UI)

      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);
      const bankId             = newUrlSearchParams.has('bankId')
        ? newUrlSearchParams.get('bankId')
        : 'fde5ed9d-816f-43fd-89f3-97a542947193';
      const domainUrl          = this.$store.getters.getBMCInstance === 'marketingcenter.source4'
        ? this.$store.getters.getBMCInstance
        : `www.${this.$store.getters.getBMCInstance}`;
      const url                = `${this.$store.getters.getBaseUrl}/web_service.aspx?method=GenerateAiImageThumbnails&domainURL=https://${domainUrl}.com/&bankId=${bankId}&userInput=${this.description.trim()}`;

      let completedCalls = 0;
      const totalCalls   = 4;

      const makeRequest = () => new Promise((resolve, reject) => {
        const oReq = new XMLHttpRequest();
        oReq.addEventListener('load', (res) => {
          try {
            const parser     = new DOMParser();
            const xmlDoc     = parser.parseFromString(res.srcElement.response, 'text/xml');
            const resultNode = xmlDoc.getElementsByTagNameNS(
              'http://www.bankmarketingcenter.com',
              'GenerateAiImageThumbnailsResult',
            )[0] || xmlDoc.evaluate(
              '//*[local-name()="GenerateAiImageThumbnailsResult"]',
              xmlDoc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null,
            ).singleNodeValue;

            if (resultNode.innerHTML.includes('No OpenAI credits')) {
              this.creditsError = resultNode.innerHTML;
              this.creditsError = 'Insufficient credits. Contact customer success to add more.';
              this.isLoading    = false;
              return;
            }

            if (resultNode.innerHTML.includes('error')) {
              this.generationError = 'Your request was rejected as a result of our safety system. Your prompt may contain text that is not allowed by our safety system.';
              this.isLoading       = false;
              return;
            }

            if (!resultNode) { throw new Error('Result node not found'); }
            const arr  = JSON.parse(resultNode.textContent.trim());
            const urls = Array.isArray(arr) ? arr : [];

            // Add immediately to UI
            this.allUrls.push(...urls);

            // Preload just this batch
            this.preloadImages(urls, completedCalls).finally(() => {
              completedCalls += 1;
              if (completedCalls === totalCalls) {
                this.isLoading = false;
                this.updateCredits();
              }
            });

            resolve();
          } catch (err) {
            reject(err);
          }
        });
        oReq.addEventListener('error', reject);
        oReq.open('GET', url);
        oReq.send();
      });

      // fire off 4 parallel calls
      for (let i = 0; i < totalCalls; i++) {
        makeRequest().catch((err) => {
          console.error(err);
        });
      }
    },
    generateImageBk() {
      if (this.description.trim() === '') {
        this.showDescriptionError = true;
        return;
      }
      if (!this.acceptTerms) {
        this.showTermsError = true;
        return;
      }
      this.isLoading           = true;
      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);
      const bankId             = newUrlSearchParams.has('bankId') ? newUrlSearchParams.get('bankId') : 'fde5ed9d-816f-43fd-89f3-97a542947193';
      const domainUrl          = this.$store.getters.getBMCInstance === 'marketingcenter.source4' ? this.$store.getters.getBMCInstance : `www.${this.$store.getters.getBMCInstance}`;
      const url                = `${this.$store.getters.getBaseUrl}/web_service.aspx?method=GenerateAiImageThumbnails&domainURL=https://${domainUrl}.com/&bankId=${bankId}&userInput=${this.description.trim()}`;
      const oReq               = new XMLHttpRequest();
      oReq.addEventListener('load', (res) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(res.srcElement.response, 'text/xml');
        // const nodes  = xmlDoc.getElementsByTagName('string');
        const resultNode = xmlDoc.getElementsByTagNameNS(
          'http://www.bankmarketingcenter.com',
          'GenerateAiImageThumbnailsResult',
        )[0] || xmlDoc.evaluate(
          '//*[local-name()="GenerateAiImageThumbnailsResult"]',
          xmlDoc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null,
        ).singleNodeValue;

        if (!resultNode) { throw new Error('Result node not found'); }
        const arr  = JSON.parse(resultNode.textContent.trim());
        const urls = Array.isArray(arr) ? arr : [];

        this.showResult = true;
        this.showForm   = false;

        this.preloadImages(urls).then(() => {
          this.isLoading = false;
        });
      });
      oReq.addEventListener('error', (e) => {
        console.error(e);
      });
      oReq.open('GET', url);
      oReq.send();
    },
    async addToCanvas(index) {
      EventBus.$emit('showHideLoader', true);
      this.saveAiGeneratedImage(index);
      this.$store.commit('activeProduct/updateTotalUploadFiles', 1);
      const self    = this;
      const { url } = self.images[index];

      await this.uploadFileToUserGallery(url);
    },

    async uploadFileToUserGallery(url) {
      console.log('uploadFileToUserGallery', url);

      const response = await fetch(url);
      const blob     = await response.blob();

      // Convert Blob to File (so it behaves like a real uploaded file)
      const file = new File([blob], 'myImage.png', { type: blob.type });
      this.getBase64(file);
    },

    getBase64(file) {
      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);
      const userId             = newUrlSearchParams.has('userId') ? newUrlSearchParams.get('userId') : '{e8c7bbd8-b238-4553-b8b9-6801df82a92d}';

      const regextext = /[/\\?%*:|"<>@!~`()• ]/g;
      regextext.test(file.name) && (file =  new File([file.slice(0, file.size, file.type)], file.name.replaceAll(regextext, '-'), { type: file.type }));
      const imageData                 = {
        Id:                   '',
        Name:                 file.name,
        DefaultImageUrl:      '',
        ImageType:            'Image',
        CropOption:           'NotSpecified',
        ImageThumbnailPath:   '',
        ImageThumbnailPathV2: '',
      };
      const imageExtension            = imageData.Name.substring(imageData.Name.lastIndexOf('.') + 1, imageData.Name.length);
      const imageNameWithOutExtension = imageData.Name.substring(0, imageData.Name.lastIndexOf('.'));
      const reqFormData               = new FormData();
      reqFormData.append('file', file);
      const headers = {
        'Content-Type': 'text/plain',
      };

      const serverURL = (this.isSubdomain(this.$store.getters.getBMCInstance)) ? this.$store.getters.getBMCInstance : `www.${this.$store.getters.getBMCInstance}`;
      axios.post(`https://${serverURL}.com/webservices/UploadImages.ashx?userId=${userId}`, reqFormData, { headers })
        .then(async (res) => {
          const url                         = res.data;
          imageData.Id                      = url.substring(url.lastIndexOf('#') + 1, url.length);
          const returnedName                = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('#'));
          imageData.previousDefaultImageUrl = `https://www.bankmarketingcenter.com/temp/uploadedimages/${imageData.Id
          }${imageNameWithOutExtension}-resized-600.${imageExtension}#${imageData.Id}`;
          imageData.ImageThumbnailPath      = imageExtension === 'svg' ? url : `https://www.bankmarketingcenter.com/thumbnails/80-80--temp-uploadedimages-${
            returnedName}`;
          imageData.ImageThumbnailPathV2    = imageData.ImageThumbnailPath;
          imageData.DefaultImageUrl         = url.substring(0, url.lastIndexOf('#'));
          if (!this.isAddNewImageModal) {
            EventBus.$emit('onImageSelected', imageData);
            this.$store.commit('activeProduct/updateTotalUploadFiles', 1);
          } else {
            EventBus.$emit('AddImageNewImageToMyGalleryImages', imageData);
            setTimeout(() => {
              EventBus.$emit('showHideLoader', false);
              EventBus.$emit('onAddingNewObjectImage', imageData);
              this.$emit('close');
            }, 100);
          }
        })
        .catch((e) => {
          EventBus.$emit('showHideLoader', false);
          throw new Error(e.message);
        });
    },

    async preloadImages(urls, dummyIdx) {
      const self     = this;
      const promises = await urls.map(
        url => new Promise((resolve, reject) => {
          const img = new Image();
          img.src   = url;

          img.onload  = () => {
            self.images.push({
              id:          dummyIdx + 1,
              url,
              width:       img.naturalWidth,
              height:      img.naturalHeight,
              title:       'AI Image',
              description: 'none',
            });
            resolve(img);
          };
          img.onerror = () => {
            reject(url);
          };
        }),
      );

      return Promise.allSettled(promises).finally(() => {
        // this.isLoading = false;
      });
    },

    async saveAiGeneratedImage(index) {
      const imageUrl           = encodeURIComponent(this.images[index].url.replace(/&/g, '&amp;'));
      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);
      const bankId             = newUrlSearchParams.has('bankId') ? newUrlSearchParams.get('bankId') : 'fde5ed9d-816f-43fd-89f3-97a542947193';
      const domainUrl          = this.$store.getters.getBMCInstance === 'marketingcenter.source4' ? this.$store.getters.getBMCInstance : `www.${this.$store.getters.getBMCInstance}`;
      const url                = `${this.$store.getters.getBaseUrl}/web_service.aspx?method=SaveAiGeneratedImage&domainURL=https://${domainUrl}.com/&bankId=${bankId}&url=${imageUrl}`;
      const oReq               = new XMLHttpRequest();
      oReq.addEventListener('load', (res) => {
        // console.log('saveAiGeneratedImage done ', res);
      });
      oReq.addEventListener('error', (e) => {
        console.error(e);
      });
      oReq.open('GET', url);
      oReq.send();
    },

    updateCredits() {
      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);
      const bankId             = newUrlSearchParams.has('bankId') ? newUrlSearchParams.get('bankId') : 'fde5ed9d-816f-43fd-89f3-97a542947193';
      const domainUrl          = this.$store.getters.getBMCInstance === 'marketingcenter.source4' ? this.$store.getters.getBMCInstance : `www.${this.$store.getters.getBMCInstance}`;
      const url                = `${this.$store.getters.getBaseUrl}/web_service.aspx?method=GetBankAiCreditsInfo&domainURL=https://${domainUrl}.com/&bankId=${bankId}`;
      const oReq               = new XMLHttpRequest();
      oReq.addEventListener('load', (res) => {
        console.log('updateCredits done ', res);
        const parser          = new DOMParser();
        const xmlDoc          = parser.parseFromString(res.srcElement.response, 'text/xml');
        const jsonString      = xmlDoc.getElementsByTagName('GetBankAiCreditsInfoResult')[0].textContent.trim();
        const data            = JSON.parse(jsonString);
        this.maxCredits       = data.MaxCredits;
        this.remainingCredits = data.RemainingCredits;
      });
      oReq.addEventListener('error', (e) => {
        console.error(e);
      });
      oReq.open('GET', url);
      oReq.send();
    },

    isSubdomain(url) {
      url         = `https://${url}.com`;
      const regex = new RegExp(/^([a-z]+\:\/{2})?([\w-]+\.[\w-]+\.\w+)$/);
      return !!url.match(regex); // make sure it returns boolean
    },

  },
};
</script>
<style lang="scss" scoped>
  .ai-panel {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    font-family: 'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
    color: #14161B;
    -webkit-font-smoothing: antialiased;
  }

  .ai-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    min-height: 0;
    overflow: auto;
  }

  .ai-form {
    width: min(640px, 100%);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 8px;
  }

  .ai-label {
    display: block;
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: #14161B;
  }

  .ai-textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    height: 104px;
    resize: vertical;
    padding: 10px 12px;
    border: 0;
    border-radius: 10px;
    background: #fff;
    box-shadow: inset 0 0 0 1px #E7E9ED;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    color: #14161B;
    outline: 0;
  }

  .ai-textarea::placeholder {
    color: #8B919C;
  }

  .ai-textarea,
  .ai-textarea:focus {
    border: 0;
    -webkit-appearance: none;
    appearance: none;
  }

  .ai-textarea:focus {
    box-shadow: inset 0 0 0 1.5px #1570EF, 0 0 0 3px rgba(21, 112, 239, 0.15);
  }

  .ai-textarea.is-invalid,
  .ai-textarea.is-invalid:focus {
    box-shadow: inset 0 0 0 1.5px #D92D20;
  }

  .ai-field-error,
  .ai-terms-error {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.3;
    color: #D92D20;
  }

  .ai-notice {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #F3F4F6;
    font-size: 12.5px;
    line-height: 1.55;
    color: #3D4350;
  }

  .ai-notice p {
    margin: 0;
  }

  .ai-notice-cta {
    font-weight: 600;
    color: #14161B;
  }

  .ai-accept {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    margin: 0;
    font-size: 13.5px;
    font-weight: 500;
    color: #14161B;
    cursor: pointer;
  }

  .ai-accept input,
  .ai-accept input:focus {
    width: 16px;
    height: 16px;
    margin: 0;
    border: 0;
    outline: none;
    accent-color: #1570EF;
    -webkit-appearance: checkbox;
    appearance: checkbox;
  }

  .ai-generate-row {
    display: flex;
    justify-content: center;
  }

  .ai-generate,
  .ai-new-prompt,
  .ai-zoom,
  .ai-tile-hit {
    font-family: inherit;
  }

  .ai-generate {
    height: 38px;
    min-height: 38px;
    min-width: 72px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 14px;
    border: 0;
    border-radius: 9px;
    background: #1570EF;
    color: #fff;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }

  .ai-generate,
  .ai-generate:focus,
  .ai-new-prompt,
  .ai-new-prompt:focus,
  .ai-tile-hit,
  .ai-tile-hit:focus {
    border: 0;
    -webkit-appearance: none;
    appearance: none;
  }

  .ai-zoom:focus {
    border: 1px solid #D5D9E0;
    outline: none;
  }

  .ai-generate:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .ai-alert {
    display: flex;
    gap: 10px;
    padding: 11px 12px;
    border-radius: 10px;
    background: #FEF3F2;
    box-shadow: inset 0 0 0 1px #FBC9C4;
    font-size: 13px;
    line-height: 1.5;
    color: #912018;
  }

  .ai-alert ::v-deep .icon {
    color: #D92D20;
    padding-top: 1px;
  }

  .ai-results {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
  }

  .ai-results-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .ai-prompt {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.3;
    color: #14161B;
  }

  .ai-prompt-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-new-prompt {
    height: 32px;
    min-height: 32px;
    flex-shrink: 0;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 11px;
    border: 0;
    border-radius: 9px;
    background: #fff;
    color: #14161B;
    box-shadow: inset 0 0 0 1px #D5D9E0;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }

  .ai-new-prompt:hover {
    background: #F3F4F6;
  }

  .ai-grid {
    width: min(760px, 100%);
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    align-items: start;
  }

  .ai-tile {
    position: relative;
    border-radius: 12px;
    transition: transform 0.3s ease;
  }

  .image-wrapper.zoomed {
    transform: scale(1.35);
    transform-origin: center center;
    z-index: 2;
  }

  .ai-tile-hit {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: #F3F4F6;
    border-radius: 12px;
    cursor: pointer;
    overflow: hidden;
  }

  .ai-tile-hit img {
    display: block;
    width: 100%;
    height: auto;
    background: #F3F4F6;
    border-radius: 12px;
  }

  .ai-zoom {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #D5D9E0;
    border-radius: 8px;
    background: #fff;
    color: #3D4350;
    cursor: pointer;
  }

  .ai-zoom:hover {
    background: #F3F4F6;
  }

  .ai-badge {
    position: absolute;
    left: 10px;
    bottom: 10px;
    padding: 3px 8px;
    border-radius: 6px;
    background: rgba(20, 22, 27, 0.55);
    font-size: 11.5px;
    font-weight: 600;
    color: #fff;
    pointer-events: none;
  }

  .ai-skel {
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(90deg, #EEF0F3 0, #F6F7F9 40%, #EEF0F3 80%);
    background-size: 800px 100%;
    animation: ai-shimmer 1.4s linear infinite;
  }

  .ai-spin {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid #E7E9ED;
    border-top-color: #1570EF;
    box-sizing: border-box;
    flex-shrink: 0;
    animation: ai-spin 0.9s linear infinite;
  }

  .ai-credits {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    gap: 8px;
    padding-top: 14px;
    border-top: 1px solid #E7E9ED;
    font-size: 12.5px;
    line-height: 1.4;
    color: #5E6573;
  }

  .ai-credits b {
    color: #14161B;
    font-family: 'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace;
    font-weight: 700;
  }

  .ai-panel ::v-deep .icon {
    min-width: 0;
    min-height: 0;
    flex-shrink: 0;
    color: inherit;
  }

  .ai-panel ::v-deep .icon svg {
    display: block;
  }

  .ai-generate ::v-deep .icon svg,
  .ai-new-prompt ::v-deep .icon svg,
  .ai-zoom ::v-deep .icon svg,
  .ai-alert ::v-deep .icon svg {
    width: 16px;
    height: 16px;
  }

  .ai-credits ::v-deep .icon svg {
    width: 14px;
    height: 14px;
  }

  .ai-field-error ::v-deep .icon svg {
    width: 13px;
    height: 13px;
  }

  @keyframes ai-spin {
    to { transform: rotate(360deg); }
  }

  @keyframes ai-shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }

  @media screen and (max-width: 640px) {
    .ai-grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
